import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_WHERE_TO_GO_ITEM, DELETE_WHERE_TO_GO_ITEM } from '../graphql/whereToGo';
import { toast } from 'react-hot-toast';

export default function WhereToGoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_WHERE_TO_GO_ITEM, {
    variables: { id },
    skip: !id
  });

  const [deleteItem, { loading: deleting }] = useMutation(DELETE_WHERE_TO_GO_ITEM, {
    onCompleted: () => {
      toast.success('Location deleted successfully');
      navigate('/dashboard/where-to-go');
    },
    onError: (error) => {
      toast.error(`Error deleting location: ${error.message}`);
    }
  });

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      deleteItem({ variables: { id } });
    }
  };

  // Helper function to format the area value for display
  const formatArea = (area) => {
    if (!area) return null;
    
    switch (area) {
      case 'KIAWAH':
        return 'Kiawah';
      case 'ISLE_OF_PALMS':
        return 'Isle of Palms';
      case 'ISLE_OF_PALMS_WILD_DUNES':
        return 'Isle of Palms: Wild Dunes';
      case 'SEABROOK_ISLAND':
        return 'Seabrook Island';
      default:
        return area;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Error loading location: {error.message}
        </div>
      </div>
    );
  }

  if (!data?.whereToGoItem) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Location not found.
        </div>
      </div>
    );
  }

  const item = data.whereToGoItem;

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header with buttons */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Link 
              to="/dashboard/where-to-go"
              className="text-sky-600 hover:text-sky-800"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-semibold text-gray-800">{item.title}</h1>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/dashboard/where-to-go/${id}/edit`}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 focus:outline-none"
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {/* Image section */}
        {item.imageUrl && (
          <div className="w-full h-96 overflow-hidden">
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Main content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left column: Basic info */}
            <div className="md:col-span-2 space-y-6">
              {(item.categories?.length > 0 || !item.isActive) && (
                <div className="flex flex-wrap gap-1">
                  {item.categories?.map((cat) => (
                    <span key={cat.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                      {cat.name}
                    </span>
                  ))}
                  {!item.isActive && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Inactive
                    </span>
                  )}
                </div>
              )}

              {item.description && (
                <div>
                  <h2 className="text-lg font-medium text-gray-800 mb-2">Description</h2>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              )}

              {item.tips && (
                <div>
                  <h2 className="text-lg font-medium text-gray-800 mb-2">Tips & Recommendations</h2>
                  <p className="text-gray-600">{item.tips}</p>
                </div>
              )}

              {(item.promotionBannerText || item.promotionDetails) && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                  <h2 className="text-lg font-medium text-gray-800 mb-2">Promotion</h2>
                  {item.promotionBannerText && (
                    <div className="mb-3">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Banner Text</h3>
                      <div className="flex items-center gap-3">
                        {item.promotionColor && (
                          <span
                            className="inline-block text-sm font-semibold text-white px-3 py-1.5 rounded"
                            style={{ backgroundColor: item.promotionColor }}
                          >
                            {item.promotionBannerText}
                          </span>
                        )}
                        {!item.promotionColor && (
                          <p className="text-gray-800">{item.promotionBannerText}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {item.promotionDetails && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Details</h3>
                      <p className="text-gray-600 whitespace-pre-wrap">{item.promotionDetails}</p>
                    </div>
                  )}
                  {item.promotionColor && (
                    <div className="mt-2">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Banner Color</h3>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: item.promotionColor }}
                        />
                        <span className="text-sm text-gray-600 font-mono">{item.promotionColor}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right column: Contact info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg font-medium text-gray-800 mb-4">Contact & Location</h2>
              
              <div className="space-y-3">
                {item.area && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Area</h3>
                    <p className="text-gray-800">{formatArea(item.area)}</p>
                  </div>
                )}

                {(item.address || item.city || item.state || item.zipCode) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Address</h3>
                    <p className="text-gray-800">
                      {[item.address, item.city, item.state].filter(Boolean).join(', ')} {item.zipCode || ''}
                    </p>
                  </div>
                )}

                {item.phoneNumber && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                    <p className="text-gray-800">
                      <a href={`tel:${item.phoneNumber}`} className="text-sky-600 hover:text-sky-800">
                        {item.phoneNumber}
                      </a>
                    </p>
                  </div>
                )}

                {item.website && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Website</h3>
                    <p className="text-gray-800">
                      <a 
                        href={item.website} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-sky-600 hover:text-sky-800 break-words"
                      >
                        {item.website.replace(/^https?:\/\//, '')}
                      </a>
                    </p>
                  </div>
                )}
                
                {(item.address || item.city || item.state) && (
                  <div className="pt-2">
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        [item.address, item.city, item.state, item.zipCode].filter(Boolean).join(', ')
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      View on Map
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 